import { clsx } from "clsx";
import {
	forwardRef,
	type ReactNode,
	type ComponentPropsWithoutRef,
} from "react";
import { SortingArrowButton } from "@exopengithub/exokit";
import type { UseSortColumnProps } from "src/common/utils/hooks/useSort";

type GridHeaderProps = ComponentPropsWithoutRef<"div"> & {
	children?: ReactNode | undefined;
};

export const GridHeader = forwardRef<HTMLDivElement, GridHeaderProps>(
	function GridHeader({ children, className, ...props }, ref) {
		return (
			<div
				ref={ref}
				className={clsx(
					"sticky top-0 z-20 border-b border-b-gray-200 bg-gray-50 px-3",
					className,
				)}
				{...props}
			>
				{children}
			</div>
		);
	},
);

type GridHeaderLabelProps = ComponentPropsWithoutRef<"div"> & {
	endAdornment?: ReactNode;
	children?: ReactNode | undefined;
	align?: "left" | "right";
	afterLabel?: ReactNode;
	textWeight?: "medium" | "bold" | undefined;
};

export const GridHeaderLabel = forwardRef<HTMLDivElement, GridHeaderLabelProps>(
	function GridHeaderLabel(
		{
			children,
			endAdornment,
			afterLabel,
			align = "left",
			textWeight = "medium",
			...props
		},
		ref,
	) {
		return (
			<GridHeader ref={ref} {...props}>
				<div
					className={clsx(
						"flex w-full items-center gap-1 py-3",
						align === "left" ? "justify-start" : "justify-end",
					)}
				>
					<div
						className={clsx(
							"text-xs text-gray-500 truncate",
							textWeight === "medium" ? "font-medium" : "font-bold",
						)}
					>
						{children}
					</div>
					{endAdornment}
				</div>
				{afterLabel}
			</GridHeader>
		);
	},
);

type GridHeaderLabelWithSortingButtonProps = GridHeaderLabelProps & {
	sortingProps?: UseSortColumnProps | undefined;
};

export const GridHeaderLabelWithSortingButton = forwardRef<
	HTMLDivElement,
	GridHeaderLabelWithSortingButtonProps
>(function GridHeaderLabelWithSortingButton(
	{ sortingProps, endAdornment, children, className, ...props },
	ref,
) {
	return (
		<GridHeaderLabel
			{...props}
			className={clsx("group", className)}
			ref={ref}
			endAdornment={
				<>
					{endAdornment}
					{sortingProps && (
						<SortingArrowButton
							variant="sort"
							className="text-gray-500"
							{...sortingProps}
						/>
					)}
				</>
			}
		>
			{children}
		</GridHeaderLabel>
	);
});
